import React, { useState, useEffect } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-react';

export default function AssignmentTable({ classId, onUpdate }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [formData, setFormData] = useState({
    assignmentName: '',
    pointsEarned: '',
    pointsPossible: '',
    date: ''
  });

  useEffect(() => {
    loadAssignments();
  }, [classId]);

  const loadAssignments = async () => {
    try {
      const data = await api.getAssignments(classId);
      setAssignments(data);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.assignmentName.trim()) {
      toast.error('Assignment name is required');
      return;
    }
    
    const pointsEarned = parseFloat(formData.pointsEarned);
    const pointsPossible = parseFloat(formData.pointsPossible);
    
    if (isNaN(pointsEarned) || pointsEarned < 0) {
      toast.error('Points earned must be a non-negative number');
      return;
    }
    
    if (isNaN(pointsPossible) || pointsPossible < 0) {
      toast.error('Points possible must be a non-negative number');
      return;
    }

    try {
      if (editingAssignment) {
        // Update
        await api.updateAssignment(editingAssignment.id, {
          assignmentName: formData.assignmentName.trim(),
          pointsEarned,
          pointsPossible,
          date: formData.date || null
        });
        toast.success('Assignment updated successfully');
      } else {
        // Create
        await api.createAssignment(classId, {
          assignmentName: formData.assignmentName.trim(),
          pointsEarned,
          pointsPossible,
          date: formData.date || null
        });
        toast.success('Assignment added successfully');
      }
      
      // Reload assignments and update parent component
      await loadAssignments();
      await onUpdate();
      
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.message || 'Failed to save assignment');
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await api.deleteAssignment(assignmentId);
      // Reload assignments and update parent component
      await loadAssignments();
      await onUpdate();
      toast.success('Assignment deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete assignment');
    }
  };

  const openEditDialog = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      assignmentName: assignment.assignmentName,
      pointsEarned: assignment.pointsEarned.toString(),
      pointsPossible: assignment.pointsPossible.toString(),
      date: assignment.date || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAssignment(null);
    setFormData({
      assignmentName: '',
      pointsEarned: '',
      pointsPossible: '',
      date: ''
    });
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Assignments</h3>
        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-assignment-button">
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>{editingAssignment ? 'Edit Assignment' : 'Add Assignment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="assignmentName" className="text-sm font-medium">
                  Assignment Name
                </label>
                <Input
                  id="assignmentName"
                  placeholder="Homework 1, Midterm, etc."
                  value={formData.assignmentName}
                  onChange={(e) => setFormData({ ...formData, assignmentName: e.target.value })}
                  data-testid="assignment-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="pointsEarned" className="text-sm font-medium">
                    Points Earned
                  </label>
                  <Input
                    id="pointsEarned"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="85"
                    value={formData.pointsEarned}
                    onChange={(e) => setFormData({ ...formData, pointsEarned: e.target.value })}
                    data-testid="assignment-earned-input"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="pointsPossible" className="text-sm font-medium">
                    Points Possible
                  </label>
                  <Input
                    id="pointsPossible"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="100"
                    value={formData.pointsPossible}
                    onChange={(e) => setFormData({ ...formData, pointsPossible: e.target.value })}
                    data-testid="assignment-possible-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="date" className="text-sm font-medium">
                  Date (Optional)
                </label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="assignment-date-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-assignment-button">
                {editingAssignment ? 'Update Assignment' : 'Add Assignment'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading assignments...</div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No assignments yet. Add your first assignment to see your grade.</p>
        </div>
      ) : (
        <div className="rounded-md border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Earned</TableHead>
                <TableHead className="text-right">Possible</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => {
                const percent = assignment.pointsPossible > 0
                  ? (assignment.pointsEarned / assignment.pointsPossible * 100)
                  : 0;
                return (
                  <TableRow key={assignment.id} data-testid="assignment-row">
                    <TableCell className="font-medium">{assignment.assignmentName}</TableCell>
                    <TableCell className="text-right">{assignment.pointsEarned}</TableCell>
                    <TableCell className="text-right">{assignment.pointsPossible}</TableCell>
                    <TableCell className="text-right">{percent.toFixed(1)}%</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => openEditDialog(assignment)}
                          data-testid="assignment-edit-button"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 text-sm"
                          onClick={() => handleDelete(assignment.id)}
                          data-testid="assignment-delete-button"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}